import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Box, Stack, Text, ThemeIcon } from '@mantine/core';
import { IconChecklist } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import type { FormSpec } from '../../../lib/form-specs';
import { FieldCanvasCard } from './FieldCanvasCard';

export interface FieldCanvasProps {
  spec: FormSpec | null;
  selectedKey: string | null;
  onSelect: (key: string) => void;
  onReorder: (oldIndex: number, newIndex: number) => void;
  onDuplicate: (key: string) => void;
  onDelete: (key: string) => void;
}

const fieldNeedsOptions = (fieldType: string) =>
  fieldType === 'select' || fieldType === 'radio' || fieldType === 'checkbox';

export function FieldCanvas({
  spec,
  selectedKey,
  onSelect,
  onReorder,
  onDuplicate,
  onDelete,
}: FieldCanvasProps) {
  const { t } = useTranslation();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const fieldKeys = spec?.fields ? Object.keys(spec.fields) : [];

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = fieldKeys.indexOf(active.id as string);
    const newIndex = fieldKeys.indexOf(over.id as string);
    if (oldIndex < 0 || newIndex < 0) return;
    onReorder(oldIndex, newIndex);
  };

  if (!spec || fieldKeys.length === 0) {
    return (
      <Stack align="center" justify="center" gap="md" p="xl" style={{ minHeight: 320 }}>
        <ThemeIcon variant="light" size={48} radius="xl" color="gray">
          <IconChecklist size={28} />
        </ThemeIcon>
        <Text c="dimmed" ta="center">
          {t('fieldEditor.canvasEmptyState')}
        </Text>
      </Stack>
    );
  }

  return (
    <Box style={{ maxWidth: 720, margin: '0 auto', width: '100%' }} p="md">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={fieldKeys} strategy={verticalListSortingStrategy}>
          <Stack gap="md">
            {fieldKeys.map((key) => {
              const field = spec.fields[key];
              const hasError =
                fieldNeedsOptions(field.field) && (!field.options || field.options.length < 2);
              return (
                <FieldCanvasCard
                  key={key}
                  fieldKey={key}
                  field={field}
                  selected={selectedKey === key}
                  hasError={hasError}
                  onSelect={() => onSelect(key)}
                  onDuplicate={() => onDuplicate(key)}
                  onDelete={() => onDelete(key)}
                />
              );
            })}
          </Stack>
        </SortableContext>
      </DndContext>
    </Box>
  );
}
